import React, { useLayoutEffect, useMemo, useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { Base, Geometry, Subtraction } from '@react-three/csg'
import WallShaderMaterial from '../../materials/WallShaderMaterial'
import { extend } from '@react-three/fiber'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import applyNormalDirectionFilter from "../../utils/normalDirectionFilter"
import { getRoofPerimeterWorld } from "../../utils/roofEdgesUtils"

extend({ WallShaderMaterial })

// ✅ Czyste helper functions — poza komponentem, stabilne referencje
function boxCenter(geometry) {
  if (!geometry.boundingBox) geometry.computeBoundingBox()
  const c = new THREE.Vector3()
  geometry.boundingBox.getCenter(c)
  return c
}

function unionBoxes(geometries) {
  const box = new THREE.Box3()
  geometries.forEach((g) => {
    if (!g.boundingBox) g.computeBoundingBox()
    box.union(g.boundingBox)
  })
  return box
}

function applyRepeat(tex, rx, ry) {
  if (!tex) return
  setupTextureRepeat(tex, rx, ry)
}

// ✅ Helper do budowy subtraction geometries — eliminuje duplikację
function buildSubtractions(sources) {
  const tmp = []
  sources.forEach((dataArray) => {
    if (!dataArray?.length) return
    dataArray.forEach((wallData) => {
      if (!wallData.closedBoundingBox) return
      const { size } = wallData.closedBoundingBox
      tmp.push({
        geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
        position: wallData.positionAdjusted || [0, 0, 0],
        scale: wallData.scale,
      })
    })
  })
  return tmp
}

const EPS = 0.02

export default function GarazScianyDach({
                                          gatesData,
                                          doorsData,
                                          windowsData,
                                          textures,
                                          onTrimsData,
                                          ...props
                                        }) {
  const { nodes, materials } = useGLTF('/models/garages/spad-tyl-v3.glb')

  // ===== Scaled geometries =====
  const scaledRightGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Right_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    g.computeBoundingBox()
    return g
  }, [nodes.Right_side.geometry, props.length, props.height, props.width])

  const scaledLeftGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Left_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    g.computeBoundingBox()
    return g
  }, [nodes.Left_side.geometry, props.length, props.height, props.width])

  const scaledBackGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Back_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    g.computeBoundingBox()
    return g
  }, [nodes.Back_side.geometry, props.length, props.height, props.width])

  const scaledFrontGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Front_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    g.computeBoundingBox()
    return g
  }, [nodes.Front_side.geometry, props.length, props.height, props.width])

  const scaledRoofGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Celing.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.width / 100
    )
    g.computeBoundingBox()
    return g
  }, [nodes.Celing.geometry, props.length, props.height, props.width])

  // ✅ Jeden useMemo zamiast 5 (garageCenter + 4 kierunki)
  const { garageCenter, dirLeft, dirRight, dirFront, dirBack } = useMemo(() => {
    const box = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
    const center = new THREE.Vector3()
    box.getCenter(center)

    return {
      garageCenter: center,
      dirLeft: boxCenter(scaledLeftGeometry).sub(center.clone()).normalize(),
      dirRight: boxCenter(scaledRightGeometry).sub(center.clone()).normalize(),
      dirFront: boxCenter(scaledFrontGeometry).sub(center.clone()).normalize(),
      dirBack: boxCenter(scaledBackGeometry).sub(center.clone()).normalize(),
    }
  }, [scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])

  // ✅ useMemo zamiast useState + useEffect — zero dodatkowych renderów
  const flattenedWindows = useMemo(() => {
    const result = {}
    if (!windowsData) return result

    Object.entries(windowsData).forEach(([wallKey, windowTypes]) => {
      const wallWindows = []
      Object.entries(windowTypes || {}).forEach(([typeKey, windows]) => {
        if (!Array.isArray(windows)) return
        windows.forEach((w) => wallWindows.push({ ...w, wall: wallKey, type: typeKey }))
      })
      result[wallKey] = wallWindows
    })

    return result
  }, [windowsData])

  // ✅ Zdeduplikowane subtraction geometries
  const subtractionGeometriesFrontWall = useMemo(
    () => buildSubtractions([gatesData, doorsData?.front_wall, flattenedWindows?.front_wall]),
    [gatesData, doorsData?.front_wall, flattenedWindows?.front_wall]
  )

  const subtractionGeometriesBackWall = useMemo(
    () => buildSubtractions([doorsData?.back_wall, flattenedWindows?.back_wall]),
    [doorsData?.back_wall, flattenedWindows?.back_wall]
  )

  const subtractionGeometriesLeftWall = useMemo(
    () => buildSubtractions([doorsData?.left_wall, flattenedWindows?.left_wall]),
    [doorsData?.left_wall, flattenedWindows?.left_wall]
  )

  const subtractionGeometriesRightWall = useMemo(
    () => buildSubtractions([doorsData?.right_wall, flattenedWindows?.right_wall]),
    [doorsData?.right_wall, flattenedWindows?.right_wall]
  )

  // ===== Materials =====
  const baseTemplate = useMemo(() => {
    return materials?.Material ? materials.Material : new THREE.MeshStandardMaterial()
  }, [materials])

  const cloneBase = () => baseTemplate.clone()

  const makeOuterMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_color || null

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'out', EPS)
    return m
  }

  const makeInnerMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_inside_color || null

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#f5f5f5')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'in', EPS)
    return m
  }

  const leftWallOuterMaterial = useMemo(() => makeOuterMaterial(props.length / 100, 1, dirLeft), [baseTemplate, textures?.walls_color, props.length, dirLeft])
  const leftWallInnerMaterial = useMemo(() => makeInnerMaterial(props.length / 100, 1, dirLeft), [baseTemplate, textures?.walls_inside_color, props.length, dirLeft])

  const rightWallOuterMaterial = useMemo(() => makeOuterMaterial(props.length / 100, 1, dirRight), [baseTemplate, textures?.walls_color, props.length, dirRight])
  const rightWallInnerMaterial = useMemo(() => makeInnerMaterial(props.length / 100, 1, dirRight), [baseTemplate, textures?.walls_inside_color, props.length, dirRight])

  const frontWallOuterMaterial = useMemo(() => makeOuterMaterial(1, 1, dirFront), [baseTemplate, textures?.walls_color, dirFront])
  const frontWallInnerMaterial = useMemo(() => makeInnerMaterial(1, 1, dirFront), [baseTemplate, textures?.walls_inside_color, dirFront])

  const backWallOuterMaterial = useMemo(() => makeOuterMaterial(1, 1, dirBack), [baseTemplate, textures?.walls_color, dirBack])
  const backWallInnerMaterial = useMemo(() => makeInnerMaterial(1, 1, dirBack), [baseTemplate, textures?.walls_inside_color, dirBack])

  // ===== ROOF =====
  const roofOuterDir = useMemo(() => {
    const nAttr = scaledRoofGeometry?.attributes?.normal
    if (!nAttr || !nAttr.array) return new THREE.Vector3(0, 1, 0)

    const arr = nAttr.array
    let sumY = 0
    for (let i = 1; i < arr.length; i += 3) sumY += arr[i]

    const sign = sumY >= 0 ? 1 : -1
    return new THREE.Vector3(0, sign, 0)
  }, [scaledRoofGeometry])

  const RoofMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null

    m.side = THREE.FrontSide

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    return m
  }, [baseTemplate, textures?.roof_color])

  const RoofFilcMaterial = useMemo(() => {
    if (Number(props?.moisture_protection) !== 2) return null

    const RoofColor = textures?.roof_color || null
    const filcTex = RoofColor?.texture_filc || null
    if (!filcTex) return null

    const wallsTex = textures?.walls_color || null

    try {
      filcTex.colorSpace = THREE.SRGBColorSpace
      filcTex.needsUpdate = true
    } catch (e) {}

    const m = cloneBase()
    m.side = THREE.DoubleSide

    m.map = filcTex
    applyRepeat(m.map, 1, 1)

    m.normalMap = wallsTex?.textureNormalness || null
    if (m.normalMap) {
      applyRepeat(
        m.normalMap,
        1 / (wallsTex.multiplierHorizontal || 1),
        1 / (wallsTex.multiplierVertical || 1)
      )
    }

    m.metalnessMap = null
    m.metalness = 0
    m.roughness = 1
    m.color = new THREE.Color('#ffffff')

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, textures?.walls_color, props?.moisture_protection, roofOuterDir])

  // ===== TOOLING: trims data =====
  const groupRef = useRef(null)
  const roofOuterRef = useRef(null)

  useLayoutEffect(() => {
    if (!onTrimsData) return
    if (!groupRef.current || !roofOuterRef.current) return

    groupRef.current.updateWorldMatrix(true, true)
    roofOuterRef.current.updateWorldMatrix(true, true)

    const { segments: roofEdgeSegments } = getRoofPerimeterWorld(roofOuterRef.current)

    const wallsBoxLocal = unionBoxes([
      scaledLeftGeometry,
      scaledRightGeometry,
      scaledFrontGeometry,
      scaledBackGeometry,
    ])

    const toWorldXZ = (x, z) => {
      const v = new THREE.Vector3(x, 0, z)
      return groupRef.current.localToWorld(v)
    }

    const wallsBoxWorld = wallsBoxLocal.clone().applyMatrix4(groupRef.current.matrixWorld)
    const groundY = wallsBoxWorld.min.y

    const getRoofYAtXZ = (roofMesh, x, z) => {
      const roofWorldBox = new THREE.Box3().setFromObject(roofMesh)
      const originY = roofWorldBox.max.y + 5

      const raycaster = new THREE.Raycaster()
      raycaster.set(new THREE.Vector3(x, originY, z), new THREE.Vector3(0, -1, 0))

      const hits = raycaster.intersectObject(roofMesh, false)
      if (hits && hits.length) return hits[0].point.y

      return roofWorldBox.max.y
    }

    const minX = wallsBoxLocal.min.x
    const maxX = wallsBoxLocal.max.x
    const minZ = wallsBoxLocal.min.z
    const maxZ = wallsBoxLocal.max.z

    const makeCornerTop = (xLocal, zLocal) => {
      const pWorld = toWorldXZ(xLocal, zLocal)
      const yRoof = getRoofYAtXZ(roofOuterRef.current, pWorld.x, pWorld.z)
      return new THREE.Vector3(pWorld.x, yRoof, pWorld.z)
    }

    const roofCorners = {
      frontLeft:  makeCornerTop(minX, minZ),
      frontRight: makeCornerTop(minX, maxZ),
      backLeft:   makeCornerTop(maxX, minZ),
      backRight:  makeCornerTop(maxX, maxZ),
    }

    const cornerSegments = Object.entries(roofCorners).map(([key, top]) => ({
      key,
      a: new THREE.Vector3(top.x, groundY, top.z),
      b: top.clone(),
    }))

    // =========================================================
    // ✅ NOWE: PITAGORAS - kąt nachylenia połaci + kierunek spadu
    // - bierzemy najwyższy i najniższy róg dachu (world)
    // - run = dystans poziomy (XZ)
    // - dy  = różnica wysokości
    // - pitch = atan2(dy, run)
    // =========================================================
    const cornerList = Object.entries(roofCorners).map(([key, p]) => ({ key, p }))
    cornerList.sort((a, b) => b.p.y - a.p.y)

    const high = cornerList[0]?.p?.clone() || null
    const low = cornerList[cornerList.length - 1]?.p?.clone() || null

    let roofPitchRadians = 0
    let roofSlopeDir = new THREE.Vector3(1, 0, 0)

    if (high && low) {
      const dx = low.x - high.x
      const dz = low.z - high.z
      const run = Math.sqrt(dx * dx + dz * dz)
      const dy = Math.max(0, high.y - low.y)

      roofPitchRadians = Math.atan2(dy, Math.max(1e-6, run))

      // kierunek spadu: od najwyższego do najniższego w XZ
      const d = new THREE.Vector3(dx, 0, dz)
      if (d.lengthSq() > 1e-12) roofSlopeDir = d.normalize()
    }

    onTrimsData({
      roofEdges: roofEdgeSegments,
      corners: cornerSegments,
      roofCorners,
      groundY,

      // ✅ nowe dane dla DachRombek:
      roofPitchRadians,          // radiany
      roofSlopeDir,              // Vector3 world, XZ
      roofHighPoint: high,       // Vector3 world
      roofLowPoint: low,         // Vector3 world
      roofHighPoints: high ? [high.clone()] : [], // na start 1 punkt, możesz potem rozszerzyć
    })
  }, [
    onTrimsData,
    props.length,
    props.width,
    props.height,
    scaledLeftGeometry,
    scaledRightGeometry,
    scaledFrontGeometry,
    scaledBackGeometry,
  ])

  // ===== RENDER =====
  return (
    <group ref={groupRef} {...props} dispose={null} castShadow receiveShadow>
      {/* ROOF outer */}
      <mesh ref={roofOuterRef} geometry={scaledRoofGeometry} material={RoofMaterial} />

      {/* ROOF inner filc */}
      {RoofFilcMaterial && (
        <mesh geometry={scaledRoofGeometry} material={RoofFilcMaterial} />
      )}

      {/* FRONT outer */}
      <mesh name="Front_side" material={frontWallOuterMaterial}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* FRONT inner */}
      <mesh name="Front_side_inner" material={frontWallInnerMaterial}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT outer */}
      <mesh name="Left_side" material={leftWallOuterMaterial}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT inner */}
      <mesh name="Left_side_inner" material={leftWallInnerMaterial}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT outer */}
      <mesh name="Right_side" material={rightWallOuterMaterial}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT inner */}
      <mesh name="Right_side_inner" material={rightWallInnerMaterial}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK outer */}
      <mesh name="Back_side" material={backWallOuterMaterial}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK inner */}
      <mesh name="Back_side_inner" material={backWallInnerMaterial}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} />
          ))}
        </Geometry>
      </mesh>
    </group>
  )
}
